import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from "react";
import { 
  PenTool, Box, Zap, Settings2, Settings, HelpCircle, Cpu, Layers, 
  Home, Camera, BookOpen, Package, ShieldAlert, Sun, Atom, Code, 
  Scan, Rocket, Sparkles, Mic, Droplets, Search, X, ChevronRight, ChevronLeft,
  Info, LayoutGrid, Maximize2, Minimize2, CheckCircle2, ArrowRight, Compass,
  FolderKanban, Calculator, Activity, Radio, Scale, Network, Columns, Code2,
  Battery, Palette, Brain
} from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { SectionLabel } from "./Disciplines";
import { TabsSlider } from "@/components/ui/tabs-slider";
import { usePrometheusVoice } from "@/hooks/usePrometheusVoice";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Lazy-load heavy tool panels — only the active tab enters the bundle graph
const GearDesigner = lazy(() => import("./GearDesigner").then((m) => ({ default: m.GearDesigner })));
const GCodeGenerator = lazy(() => import("./GCodeGenerator").then((m) => ({ default: m.GCodeGenerator })));
const ArduinoSuite = lazy(() => import("./ArduinoSuite").then((m) => ({ default: m.ArduinoSuite })));
const SolarCalculator = lazy(() => import("./SolarCalculator").then((m) => ({ default: m.SolarCalculator })));
const EngineeringQuickTools = lazy(() =>
  import("./EngineeringQuickTools").then((m) => ({ default: m.EngineeringQuickTools }))
);
const MaterialMatrix = lazy(() => import("./MaterialMatrix").then((m) => ({ default: m.MaterialMatrix })));
const InventoryManager = lazy(() => import("./InventoryManager").then((m) => ({ default: m.InventoryManager })));
const PrometheusCanvasStudio = lazy(() =>
  import("./PrometheusCanvasStudio").then((m) => ({ default: m.PrometheusCanvasStudio }))
);
const MCPHardwareHub = lazy(() => import("./MCPHardwareHub").then((m) => ({ default: m.MCPHardwareHub })));
const TitansCouncilStudio = lazy(() =>
  import("./TitansCouncilStudio").then((m) => ({ default: m.TitansCouncilStudio }))
);
const LoRaMeshSimulator = lazy(() => import("./LoRaMeshSimulator").then((m) => ({ default: m.LoRaMeshSimulator })));
const GooglePicsStudio = lazy(() => import("./GooglePicsStudio").then((m) => ({ default: m.GooglePicsStudio })));
const PrometheusAutomationStudio = lazy(() =>
  import("./PrometheusAutomationStudio").then((m) => ({ default: m.PrometheusAutomationStudio }))
);
const ScienceLab = lazy(() => import("./ScienceLab").then((m) => ({ default: m.ScienceLab })));

export type UtilityTab =
  | "google_pics"
  | "gear"
  | "gcode"
  | "canvas"
  | "arduino"
  | "solar"
  | "quicktools"
  | "lora"
  | "materials"
  | "council"
  | "inventory"
  | "mcp"
  | "automation"
  | "science_lab";

/** Legacy tab ids that now live in Taller or /brain3d — redirect, not registry. */
const LEGACY_TAB_REDIRECT: Record<string, string> = {
  cad_bridge: "/taller?station=copilot",
  spatial_house: "/taller?station=dreamhouse",
  architecture_suite: "/taller?station=dreamhouse",
  brain3d: "/brain3d",
};

export type ToolCategory = "design" | "simulation" | "science" | "operations";

interface ToolMetadata {
  id: UtilityTab;
  title: string;
  category: ToolCategory;
  categoryLabel: string;
  icon: any;
  tagline: string;
  howToUse: string[];
  tags: string[];
  prTip?: string;
}

const TOOLS_REGISTRY: ToolMetadata[] = [
  {
    id: "automation",
    title: "Visual Automation Studio & Devices Hub",
    category: "operations",
    categoryLabel: "Operaciones & Taller",
    icon: Network,
    tagline: "Lienzo de nodos de automatización física, hardware IoT y monitor de dispositivos en tiempo real.",
    howToUse: [
      "1. Selecciona una plantilla (Cisterna PR, Extractor Taller o LUMA Apagón) o añade nodos al lienzo.",
      "2. Conecta dispositivos físicos por Web Serial USB o red local en el panel Devices.",
      "3. Enlaza los cables Bézier entre disparadores, condiciones lógicas y actuadores.",
      "4. Pulsa 'Ejecutar Simulación' para visualizar el flujo paso a paso y la telemetría en vivo."
    ],
    tags: ["automatizacion", "nodos", "flow", "devices", "dispositivos", "iot", "raspberry pi", "esp32", "web serial", "rele", "cisterna"],
    prTip: "Permite enlazar eventos del taller directamente con cotizaciones en pasillos y bahías de Home Depot Rexville #6405."
  },
  {
    id: "google_pics",
    title: "Google Pics • Serigrafía & Faceplates",
    category: "design",
    categoryLabel: "Diseño & Fabricación",
    icon: Palette,
    tagline: "Diseñador gráfico vectorial de paneles frontales, rotulado técnico y calcomanías para gabinetes 3D y corte láser.",
    howToUse: [
      "1. Selecciona la plantilla de gabinete (ESP32, LiFePO4, Solar, LoRa) o ajusta las medidas milimétricas.",
      "2. Elige el estilo visual (Dark Industrial, Hazard Striped, Titanium o Tactical).",
      "3. Descarga el SVG vectorial para corte láser/plotter o PNG de alta resolución para vinilo adhesivo."
    ],
    tags: ["google pics", "faceplate", "serigrafia", "corte laser", "vinilo", "rotulado", "gabinete", "esp32", "panel frontal"],
    prTip: "Para exteriores en Puerto Rico, lamina el vinilo impreso con capa protectora UV contra la degradación solar."
  },
  {
    id: "gear",
    title: "Generador de Engranajes",
    category: "design",
    categoryLabel: "Diseño & Fabricación",
    icon: Settings,
    tagline: "Cálculo matemático de perfiles de dientes involuta (20°), módulo y relación de transmisión.",
    howToUse: [
      "1. Ingresa el número de dientes (Z), módulo métrico y ángulo de presión.",
      "2. Verifica el diámetro primitivo, paso circular y holgura de engrane sin colisiones.",
      "3. Descarga el perfil vectorial SVG o modelo 3D STL listo para corte o impresión."
    ],
    tags: ["engranajes", "gears", "transmision", "modulo", "dientes", "mecanica"]
  },

  {
    id: "gcode",
    title: "Estudio CAD / CAM, Slicers & G-Code",
    category: "design",
    categoryLabel: "Diseño & Fabricación",
    icon: Code,
    tagline: "Scripts paramétricos OpenSCAD, perfiles Bambu/OrcaSlicer, trayectorias CNC y costos de energía LUMA.",
    howToUse: [
      "1. Elige entre scripts OpenSCAD (para Fusion 360), perfiles de filamento para Bambu/Orca o código CNC.",
      "2. Calcula el flujo volumétrico (mm³/s) y las temperaturas de secado para el clima de Puerto Rico.",
      "3. Copia o descarga los archivos .scad, .json y .nc listos para tus programas de fabricación real."
    ],
    tags: ["openscad", "bambu", "orcaslicer", "fusion360", "slicer", "cnc", "gcode", "flujo", "luma"],
    prTip: "Incluye tarifas de energía de LUMA en PR y compensación de flujo para PETG en ambiente tropical."
  },
  {
    id: "canvas",
    title: "Estudio Canvas & Artifacts 2.0",
    category: "design",
    categoryLabel: "Diseño & Fabricación",
    icon: Columns,
    tagline: "Edición quirúrgica de firmwares C++, mini-apps React y modelos OpenSCAD con sandbox interactivo side-by-side.",
    howToUse: [
      "1. Selecciona un proyecto de firmware ESP32, componente React o modelo 3D OpenSCAD.",
      "2. Aplica modificaciones quirúrgicas directamente con el asistente de IA (Claude 5 / GPT-5.6).",
      "3. Visualiza la salida interactiva en vivo y exporta el código listo para tu taller."
    ],
    tags: ["canvas", "artifacts", "editor", "c++", "react", "openscad", "sandbox", "diff", "claude 5"]
  },

  // 2. Electrónica, IoT & Energía
  {
    id: "arduino",
    title: "Suite Arduino, ESP32 & IoT",
    category: "simulation",
    categoryLabel: "Electrónica & Control",
    icon: Cpu,
    tagline: "Editor C++, simulador Wokwi, diagramas de pines GPIO, conexión a hardware real y consola UART.",
    howToUse: [
      "1. Selecciona tu placa (ESP32, ESP32-S3, Arduino Uno, Nano o Raspberry Pi Pico).",
      "2. Escribe o genera el sketch de control con IA y simúlalo interactivamente en Wokwi.",
      "3. Monitorea sensores y envía comandos UART desde la consola serial integrada."
    ],
    tags: ["arduino", "esp32", "microcontrolador", "c++", "gpio", "sensores", "serial", "uart", "wokwi", "iot"]
  },
  {
    id: "solar",
    title: "Central Solar Fotovoltaica & Respaldo LiFePO4 (PR)",
    category: "simulation",
    categoryLabel: "Electrónica & Control",
    icon: Sun,
    tagline: "Dimensionamiento fotovoltaico con 78 municipios de PR, cálculo de autonomía LiFePO4 (12V/24V/48V), inversores híbridos y ahorro en factura LUMA.",
    howToUse: [
      "1. Ingresa tu consumo eléctrico diario en kWh o lista tus electrodomésticos reales.",
      "2. Ajusta las Horas Solar Pico (HSP) promedio de tu municipio en Puerto Rico.",
      "3. Obtén la cantidad exacta de paneles, capacidad del banco LiFePO4 y breakers de protección."
    ],
    tags: ["solar", "energia solar", "paneles", "baterias", "lifepo4", "inversor", "luma", "respaldo", "breakers", "autonomia"],
    prTip: "Considera 5.2 HSP promedio en la costa sur y 4.8 HSP en el área metropolitana. Para bancos 48V usa cables 2/0 AWG estañados y fusible Class T."
  },
  {
    id: "quicktools",
    title: "Caja de Herramientas de Precisión",
    category: "simulation",
    categoryLabel: "Electrónica & Control",
    icon: Calculator,
    tagline: "Calculadoras de Ley de Ohm, divisores ADC, código de colores de resistencias, autonomía de baterías LiFePO4 y costo de impresión con tarifa LUMA.",
    howToUse: [
      "1. Calcula divisores resistivos seguros para microcontroladores ESP32 (máx 3.3V).",
      "2. Decodifica visualmente bandas de colores de resistencias de 4 franjas.",
      "3. Estima la duración de batería 18650 en ciclos de Deep Sleep y costo eléctrico en PR."
    ],
    tags: ["ohm", "resistencia", "colores", "bateria", "deep sleep", "awg", "psi", "costo impresion", "luma", "led"],
    prTip: "Incluye tarifa de LUMA PR ($0.28/kWh) y consumo en reposo de celdas LiFePO4 en clima tropical."
  },
  {
    id: "lora",
    title: "Calculadora de Radioenlace LoRa 915 MHz & Link Budget PR",
    category: "simulation",
    categoryLabel: "Electrónica & Control",
    icon: Radio,
    tagline: "Cálculo de pérdidas de trayectoria en espacio libre (FSPL), zona de Fresnel, sensibilidad SX1262 y exportación Meshtastic US915.",
    howToUse: [
      "1. Configura la potencia de transmisión (dBm), ganancia de antenas (dBi) y factor de propagación (SF7-SF12).",
      "2. Evalúa la distancia del enlace en kilómetros y el despeje de la 1ra zona de Fresnel para el relieve montañoso de PR.",
      "3. Exporta la configuración YAML verificada para firmwares Meshtastic o RadioHead en banda US915."
    ],
    tags: ["lora", "link budget", "fresnel", "915mhz", "sx1262", "meshtastic", "radioenlace", "dbm", "fspl", "offline"],
    prTip: "En Puerto Rico utiliza la banda ISM 902-928 MHz (US915). Para enlaces de larga distancia hacia la Cordillera Central, asegura al menos 60% de despeje de la 1ra zona de Fresnel."
  },

  // 3. Ciencia & Materiales Aplicados
  {
    id: "science_lab",
    title: "Science Lab — Simuladores",
    category: "science",
    categoryLabel: "Ciencia & Materiales",
    icon: Atom,
    tagline: "Simuladores científicos y de laboratorio (física, materiales, experimentos) en un solo panel.",
    howToUse: [
      "1. Elige el simulador según la disciplina del experimento.",
      "2. Ajusta parámetros y observa resultados en tiempo real.",
      "3. Exporta o anota hallazgos en tu bitácora del taller."
    ],
    tags: ["science", "ciencia", "simulador", "laboratorio", "fisica", "experimentos"],
    prTip: "Úsalo junto a Material Matrix para validar materiales resistentes a salitre y humedad en PR."
  },
  {
    id: "materials",
    title: "Laboratorio Científico & Matriz de Materiales",
    category: "science",
    categoryLabel: "Ciencia & Materiales",
    icon: Layers,
    tagline: "Propiedades termomecánicas, resistencia a corrosión por salitre marino, HDT y compatibilidad galvánica.",
    howToUse: [
      "1. Selecciona materiales (PETG, PLA, ABS, Aluminio naval, Inox 316, Concreto) para contrastar propiedades.",
      "2. Revisa la temperatura de deflexión térmica (HDT), conductividad y resistencia UV.",
      "3. Consulta la compatibilidad galvánica para evitar corrosión por contacto en clima tropical."
    ],
    tags: ["materiales", "petg", "pla", "abs", "aluminio", "acero", "resistencia", "quimica", "salitre", "inox 316"],
    prTip: "En costas de PR el salitre degrada tornillos comunes en menos de 2 años. Usa siempre Inox 316."
  },
  {
    id: "council",
    title: "El Consejo de Titanes en Vivo",
    category: "science",
    categoryLabel: "Ciencia & Materiales",
    icon: Scale,
    tagline: "Debate concurrente en tiempo real: Claude Sonnet 5, Grok 4.6 y Gemini 3.7 analizan, GPT-5.8 arbitra el consenso.",
    howToUse: [
      "1. Ingresa una consulta técnica de alta complejidad o selecciona un dilema de ingeniería predeterminado.",
      "2. Observa cómo 4 inteligencias de frontera analizan la física, el código, los datasheets y la normativa de PR en paralelo.",
      "3. Obtén el dictamen de consenso técnico sintetizado y verificado sin alucinaciones."
    ],
    tags: ["consejo", "titanes", "debate", "multi-llm", "claude 5", "grok 4.6", "gemini 3.7", "gpt-5.8", "consenso"],
    prTip: "Ideal para validar cálculos estructurales ASCE 7 de vientos de 175 mph y baterías solares antes de construir."
  },

  // 4. Operaciones & Almacén del Lab
  {
    id: "inventory",
    title: "Inventario & Guía de Abasto PR",
    category: "operations",
    categoryLabel: "Operaciones & Gestión",
    icon: Package,
    tagline: "Stock de filamentos/insumos, escáner visual por fotos (Gemini Vision) y red de ferreterías de Puerto Rico.",
    howToUse: [
      "1. Toma una foto a tu estante, caja o gaveta con '📸 Escanear con IA'.",
      "2. Revisa el stock real y marca insumos disponibles con 1 clic.",
      "3. Consulta la Guía de Abasto local de PR (Home Depot, National Lumber, Ace, USPS) para reabastecimiento."
    ],
    tags: ["inventario", "stock", "escaner fotos", "gemini vision", "tornillos", "filamento", "herramientas", "abasto pr", "home depot"]
  },
  {
    id: "mcp",
    title: "Hub de Hardware Web Serial & Protocolo MCP",
    category: "operations",
    categoryLabel: "Operaciones & Gestión",
    icon: Network,
    tagline: "Conexión USB Web Serial real a microcontroladores físicos (ESP32/Arduino/Pico) y especificaciones JSON-RPC para Claude Desktop.",
    howToUse: [
      "1. Conecta tu placa ESP32/Arduino/Pico por cable USB y pulsa 'Conectar Dispositivo Serial por USB'.",
      "2. Monitorea los registros de telemetría y envía comandos UART directamente a 115200 o 9600 baudios.",
      "3. Copia el bloque de configuración JSON-RPC MCP para conectar Claude Desktop o agentes externos a tu taller."
    ],
    tags: ["mcp", "web serial", "usb", "uart", "esp32", "arduino", "pico", "json-rpc", "anthropic", "claude"]
  },
];

interface MakerSuiteProps {
  initialTab?: string;
}

export const MakerSuite = ({ initialTab }: MakerSuiteProps = {}) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<UtilityTab>((initialTab as UtilityTab) || "gear");
  const [viewMode, setViewMode] = useState<"workspace" | "directory">("workspace");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<ToolCategory | "all">("all");
  const [isInfoOpen, setIsInfoOpen] = useState(true);
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsScrollRef.current) {
      const scrollAmount = 260;
      tabsScrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Sync initialTab with legacy redirects (stubs → Taller / Brain)
  useEffect(() => {
    if (!initialTab) return;
    const legacyTo = LEGACY_TAB_REDIRECT[initialTab];
    if (legacyTo) {
      navigate(legacyTo, { replace: true });
      return;
    }
    let mapped = initialTab as UtilityTab;
    if (initialTab === "battery_telemetry" || initialTab === "baterias") mapped = "solar";
    if (initialTab === "acoustics" || initialTab === "audio") mapped = "quicktools";
    if (TOOLS_REGISTRY.some((t) => t.id === mapped) && mapped !== activeTab) {
      setActiveTab(mapped);
      setViewMode("workspace");
    }
  }, [initialTab, navigate]);

  // Current active tool metadata
  const currentTool = useMemo(() => {
    return TOOLS_REGISTRY.find((t) => t.id === activeTab) || TOOLS_REGISTRY[0];
  }, [activeTab]);

  // Filtered tools for search / directory view
  const filteredTools = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return TOOLS_REGISTRY.filter((tool) => {
      const matchesCategory = selectedCategoryFilter === "all" || tool.category === selectedCategoryFilter;
      if (!q) return matchesCategory;
      const matchesText = 
        tool.title.toLowerCase().includes(q) ||
        tool.tagline.toLowerCase().includes(q) ||
        tool.categoryLabel.toLowerCase().includes(q) ||
        tool.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchesCategory && matchesText;
    });
  }, [searchQuery, selectedCategoryFilter]);

  // Faceplate handoff from CAD used to live here; CAD now lives in Taller.
  const faceplateTransferData: {
    widthMm?: number;
    heightMm?: number;
    title?: string;
    subtitle?: string;
    source?: string;
  } | null = null;

  // Global Hands-Free Workshop Assistant (Voice control across entire suite)
  const {
    isListening,
    isSpeaking,
    transcript,
    startListening,
    stopListening,
    speak,
    playChime
  } = usePrometheusVoice();

  const [handsFreeActive, setHandsFreeActive] = useState(false);
  const lastProcessedTranscriptRef = useRef<string>("");

  const toggleHandsFree = () => {
    if (handsFreeActive) {
      stopListening();
      setHandsFreeActive(false);
      playChime("stop");
      toast.info("🎙️ Modo Manos Libres desactivado");
    } else {
      startListening();
      setHandsFreeActive(true);
      playChime("start");
      speak("Modo taller manos libres activo. Di abrir CAD, solar, arduino, cerebro, chat o inicio.");
      toast.success("🎙️ Modo Manos Libres activo. Escuchando comandos de taller...");
    }
  };

  // Tactical Voice Command Parser (Workshop Tools & Platform Navigation)
  useEffect(() => {
    if (!handsFreeActive || !transcript || transcript === lastProcessedTranscriptRef.current) return;
    lastProcessedTranscriptRef.current = transcript;
    const lower = transcript.toLowerCase();

    // 1. Tool switching within Workshop Suite
    if (lower.includes("cad") || lower.includes("fusion") || lower.includes("blender") || lower.includes("diseño 3d")) {
      playChime("recognized");
      speak("Abriendo fotogrametría y CAD en el Taller.");
      navigate("/taller?station=copilot");
    } else if (lower.includes("carátula") || lower.includes("google pics") || lower.includes("panel") || lower.includes("serigrafía")) {
      setActiveTab("google_pics");
      setViewMode("workspace");
      playChime("recognized");
      speak("Abriendo diseñador vectorial de carátulas Google Pics.");
    } else if (lower.includes("batería") || lower.includes("luma") || lower.includes("telemetría") || lower.includes("energía") || lower.includes("solar")) {
      selectTool("solar");
      playChime("recognized");
      speak("Abriendo Central Solar Fotovoltaica y Respaldo LiFePO4.");
    } else if (lower.includes("lora") || lower.includes("radio") || lower.includes("mesh")) {
      selectTool("lora");
      playChime("recognized");
      speak("Abriendo calculadora de radioenlace LoRa 915 MHz.");
    } else if (lower.includes("mcp") || lower.includes("serial") || lower.includes("usb")) {
      selectTool("mcp");
      playChime("recognized");
      speak("Abriendo Hub de Hardware Web Serial y MCP.");
    } else if (lower.includes("arduino") || lower.includes("esp32") || lower.includes("código") || lower.includes("firmware")) {
      setActiveTab("arduino");
      setViewMode("workspace");
      playChime("recognized");
      speak("Abriendo suite de microcontroladores y firmware.");
    } else if (lower.includes("materiales") || lower.includes("salitre") || lower.includes("corrosión")) {
      setActiveTab("materials");
      setViewMode("workspace");
      playChime("recognized");
      speak("Abriendo matriz de materiales y resistencia a salitre.");
    } else if (lower.includes("cerebro") || lower.includes("bitácora") || lower.includes("segundo cerebro")) {
      playChime("recognized");
      speak("Abriendo Segundo Cerebro 3D.");
      navigate("/brain3d");
    } else if (lower.includes("inventario") || lower.includes("abasto") || lower.includes("almacén")) {
      selectTool("inventory");
      playChime("recognized");
      speak("Abriendo Inventario y Abasto.");
    } else if (lower.includes("titanes") || lower.includes("consejo")) {
      selectTool("council");
      playChime("recognized");
      speak("Abriendo Consejo de Titanes de la Ciencia.");
    } else if (lower.includes("gcode") || lower.includes("código g")) {
      selectTool("gcode");
      playChime("recognized");
      speak("Abriendo generador de código G CNC.");
    } else if (lower.includes("engranajes") || lower.includes("engranaje")) {
      selectTool("gear");
      playChime("recognized");
      speak("Abriendo diseñador de engranajes.");
    } else if (lower.includes("spatial") || lower.includes("casa 3d") || lower.includes("plano 3d") || lower.includes("roomplan") || lower.includes("escanear casa") || lower.includes("casa soñada")) {
      playChime("recognized");
      speak("Abriendo casa soñada en el Taller.");
      navigate("/taller?station=dreamhouse");
    } else if (lower.includes("automatización") || lower.includes("automatizacion") || lower.includes("nodos") || lower.includes("dispositivos") || lower.includes("devices") || lower.includes("flow")) {
      selectTool("automation");
      playChime("recognized");
      speak("Abriendo estudio de automatización visual y hub de dispositivos.");
    } else if (lower.includes("canvas") || lower.includes("pizarra")) {
      selectTool("canvas");
      playChime("recognized");
      speak("Abriendo pizarra técnica Prometheus Canvas.");
    } else if (lower.includes("directorio") || lower.includes("catálogo") || lower.includes("todas las herramientas")) {
      setViewMode("directory");
      playChime("recognized");
      speak("Mostrando catálogo completo de herramientas.");
    }
    // 2. Global Platform Navigation
    else if (lower.includes("pantalla principal") || lower.includes("ir a inicio") || lower.includes("volver a inicio") || lower.includes("home")) {
      playChime("recognized");
      speak("Navegando a la pantalla de inicio.");
      navigate("/");
    } else if (lower.includes("abrir chat") || lower.includes("ir al chat") || lower.includes("conversar")) {
      playChime("recognized");
      speak("Abriendo chat con Prometheus.");
      navigate("/");
    } else if (lower.includes("apagar voz") || lower.includes("detener manos libres") || lower.includes("silencio")) {
      toggleHandsFree();
    }
  }, [transcript, handsFreeActive]);

  const selectTool = (tabId: UtilityTab) => {
    setActiveTab(tabId);
    setViewMode("workspace");
    setSearchQuery("");
    try {
      const url = new URL(window.location.href);
      if (url.pathname.includes("/herramientas")) {
        url.searchParams.set("tab", tabId);
        window.history.replaceState(null, "", url.toString());
      }
    } catch {
      // Safe fallback
    }
  };

  const renderActiveToolComponent = () => {
    const panel = (() => {
      switch (activeTab) {
        case "google_pics":
          return <GooglePicsStudio initialData={faceplateTransferData} />;
        case "gear":
          return <GearDesigner />;
        case "gcode":
          return <GCodeGenerator />;
        case "canvas":
          return <PrometheusCanvasStudio />;
        case "arduino":
          return <ArduinoSuite />;
        case "lora":
          return <LoRaMeshSimulator />;
        case "solar":
          return <SolarCalculator />;
        case "quicktools":
          return <EngineeringQuickTools />;
        case "materials":
          return <MaterialMatrix />;
        case "science_lab":
          return <ScienceLab />;
        case "council":
          return <TitansCouncilStudio />;
        case "inventory":
          return <InventoryManager />;
        case "mcp":
          return <MCPHardwareHub />;
        case "automation":
          return <PrometheusAutomationStudio />;
        default:
          return null;
      }
    })();

    return (
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-[240px] rounded-xl border border-border bg-card/40 text-xs text-muted-foreground font-mono-tech">
            Cargando herramienta…
          </div>
        }
      >
        {panel}
      </Suspense>
    );
  };

  return (
    <section id="herramientas" className="relative py-12 md:py-20 border-t border-border bg-background text-foreground transition-colors">
      <div className="absolute inset-0 grid-blueprint opacity-10 pointer-events-none" />

      <div className="container mx-auto px-2 sm:px-4 md:px-6 relative max-w-7xl space-y-6">
        
        {/* Header with Title & Live Search */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <SectionLabel index="03" title="Herramientas & Laboratorio" />
            <h2 className="font-serif-display text-3xl md:text-5xl font-bold tracking-tight mt-1">
              Estudio de Ingeniería <span className="text-primary italic font-normal">& Fabricación</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl font-sans leading-relaxed">
              {TOOLS_REGISTRY.length} herramientas de alta precisión organizadas en 4 pilares: Diseño 3D & Mecánica, Electrónica, IoT & Radio LoRa, Ciencia, Consejo de Titanes y Almacén, MCP & Abasto PR.
            </p>
          </div>

          {/* Quick Search & View Switcher */}
          <div className="flex items-center gap-2.5">
            <div className="relative w-full md:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar herramienta (ej. Consejo, LoRa, Arduino, MCP)..."
                className="w-full bg-card border border-border pl-8 pr-3 py-2 rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary font-mono-tech shadow-xs"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Hands-Free Workshop Voice Assistant Toggle */}
            <button
              type="button"
              onClick={toggleHandsFree}
              className={`p-2 px-3 rounded-xl border font-mono-tech text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs ${
                handsFreeActive
                  ? "bg-emerald-500 text-black border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.4)] animate-pulse"
                  : "bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              title={handsFreeActive ? "Desactivar Manos Libres de Taller" : "Activar Modo Taller Manos Libres (Comandos por Voz)"}
            >
              <Mic className={`w-3.5 h-3.5 ${handsFreeActive ? "animate-bounce text-black" : "text-primary"}`} />
              <span className="text-[11px] font-bold">{handsFreeActive ? "Voz Activa" : "Manos Libres"}</span>
            </button>

            <button
              onClick={() => setViewMode(viewMode === "workspace" ? "directory" : "workspace")}
              className={`p-2 rounded-xl border font-mono-tech text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-xs ${
                viewMode === "directory"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              title="Alternar entre Directorio Completo y Espacio de Trabajo"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Hands-Free Workshop Live Command Banner */}
        {handsFreeActive && (
          <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-xl p-2.5 px-4 font-mono-tech text-[10px] text-emerald-400 flex items-center justify-between animate-in fade-in">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span><strong>Micrófono de Taller Activo:</strong> Di "abrir CAD", "solar", "arduino", "cerebro", "materiales", "chat" o "inicio"</span>
            </div>
            {transcript && (
              <span className="text-muted-foreground italic truncate max-w-xs text-[9px]">"{transcript}"</span>
            )}
          </div>
        )}

        {/* 4 Major Category Navigation Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 font-mono-tech">
          {[
            { 
              id: "design", 
              label: "Diseño 3D & Mecánica", 
              icon: Rocket, 
              count: TOOLS_REGISTRY.filter((t) => t.category === "design").length, 
              textColor: "text-sky-600 dark:text-sky-400", 
              iconBg: "bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-400",
              activeBorder: "border-sky-500 shadow-[0_0_15px_rgba(14,165,233,0.2)]"
            },
            { 
              id: "simulation", 
              label: "Electrónica, IoT & Radio LoRa", 
              icon: Zap, 
              count: TOOLS_REGISTRY.filter((t) => t.category === "simulation").length, 
              textColor: "text-emerald-600 dark:text-emerald-400", 
              iconBg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400",
              activeBorder: "border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
            },
            { 
              id: "science", 
              label: "Ciencia & Consejo Titanes", 
              icon: Atom, 
              count: TOOLS_REGISTRY.filter((t) => t.category === "science").length, 
              textColor: "text-amber-600 dark:text-amber-400", 
              iconBg: "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400",
              activeBorder: "border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.2)]"
            },
            { 
              id: "operations", 
              label: "Almacén, MCP & Abasto PR", 
              icon: Settings2, 
              count: TOOLS_REGISTRY.filter((t) => t.category === "operations").length, 
              textColor: "text-purple-600 dark:text-purple-400", 
              iconBg: "bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400",
              activeBorder: "border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.2)]"
            },
          ].map((cat) => {
            const isSelected = currentTool.category === cat.id;
            const CatIcon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategoryFilter(cat.id as any);
                  if (viewMode === "workspace") {
                    const firstInCat = TOOLS_REGISTRY.find((t) => t.category === cat.id);
                    if (firstInCat) setActiveTab(firstInCat.id);
                  }
                }}
                className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between group shadow-xs ${
                  isSelected && viewMode === "workspace"
                    ? `bg-card border ${cat.activeBorder} ring-1 ring-primary/30`
                    : "bg-card border-border hover:bg-muted/50 hover:border-border"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg border ${cat.iconBg} transition-transform group-hover:scale-105`}>
                    <CatIcon className="w-4 h-4" />
                  </div>
                  <div className="space-y-0.5">
                    <span className={`text-xs font-bold block ${cat.textColor}`}>{cat.label}</span>
                    <span className="text-[9px] text-muted-foreground">{cat.count} herramientas activas</span>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 ${isSelected ? "text-primary translate-x-0.5" : "text-muted-foreground"} transition-transform`} />
              </button>
            );
          })}
        </div>

        {/* Search Results / Directory View */}
        {(viewMode === "directory" || searchQuery.length > 0) && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div className="flex justify-between items-center bg-card border border-border p-3.5 rounded-xl font-mono-tech shadow-xs">
              <span className="text-xs font-bold text-foreground flex items-center gap-2">
                <LayoutGrid className="w-3.5 h-3.5 text-primary-glow" /> Catálogo de Herramientas ({filteredTools.length} disponibles)
              </span>
              <div className="flex items-center gap-1.5 text-[8.5px]">
                <button
                  onClick={() => setSelectedCategoryFilter("all")}
                  className={`px-2 py-1 rounded cursor-pointer ${
                    selectedCategoryFilter === "all" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setSelectedCategoryFilter("design")}
                  className={`px-2 py-1 rounded cursor-pointer ${
                    selectedCategoryFilter === "design" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Diseño
                </button>
                <button
                  onClick={() => setSelectedCategoryFilter("simulation")}
                  className={`px-2 py-1 rounded cursor-pointer ${
                    selectedCategoryFilter === "simulation" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Electrónica
                </button>
                <button
                  onClick={() => setSelectedCategoryFilter("science")}
                  className={`px-2 py-1 rounded cursor-pointer ${
                    selectedCategoryFilter === "science" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Ciencia
                </button>
                <button
                  onClick={() => setSelectedCategoryFilter("operations")}
                  className={`px-2 py-1 rounded cursor-pointer ${
                    selectedCategoryFilter === "operations" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Taller
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3.5 font-mono-tech">
              {filteredTools.map((tool) => {
                const IconComp = tool.icon;
                const isCurrent = activeTab === tool.id;
                
                const iconBadgeMap: Record<ToolCategory, string> = {
                  design: "bg-sky-500/10 border-sky-500/30 text-sky-600 dark:text-sky-400 group-hover:border-sky-500/60 group-hover:bg-sky-500/20",
                  simulation: "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 group-hover:border-emerald-500/60 group-hover:bg-emerald-500/20",
                  science: "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400 group-hover:border-amber-500/60 group-hover:bg-amber-500/20",
                  operations: "bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400 group-hover:border-purple-500/60 group-hover:bg-purple-500/20",
                };

                return (
                  <div
                    key={tool.id}
                    onClick={() => selectTool(tool.id)}
                    className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-3 group shadow-xs ${
                      isCurrent
                        ? "bg-card border-primary shadow-[0_0_15px_rgba(249,115,22,0.18)] ring-1 ring-primary/40"
                        : "bg-card border-border hover:bg-muted/40 hover:border-primary/40 hover:shadow-sm"
                    }`}
                  >
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className={`p-2 rounded-lg border ${iconBadgeMap[tool.category]} transition-transform group-hover:scale-110`}>
                            <IconComp className="w-4 h-4" />
                          </div>
                          <span className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">
                            {tool.title}
                          </span>
                        </div>
                        <span className="text-[8px] uppercase tracking-wider px-2 py-0.5 rounded bg-muted border border-border text-muted-foreground font-semibold">
                          {tool.categoryLabel}
                        </span>
                      </div>
                      <p className="text-[9.5px] text-muted-foreground font-sans line-clamp-2 leading-relaxed">
                        {tool.tagline}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border text-[9px]">
                      <span className="text-primary font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        Abrir Espacio <ArrowRight className="w-3 h-3" />
                      </span>
                      {tool.prTip && (
                        <span className="text-amber-600 dark:text-amber-400 text-[8px] font-semibold flex items-center gap-1">
                          🇵🇷 PR Optimizado
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Workspace View */}
        {viewMode === "workspace" && searchQuery.length === 0 && (
          <div className="space-y-4 animate-in fade-in duration-150">
            {/* Category Sub-Tabs Toolbar using TabsSlider with full drag-to-scroll support */}
            <div className="flex items-center gap-1.5 bg-card border border-border p-1.5 rounded-xl font-mono-tech shadow-xs">
              <TabsSlider containerClassName="flex-1 min-w-0">
                {TOOLS_REGISTRY.filter((t) => t.category === currentTool.category).map((tool) => {
                  const IconC = tool.icon;
                  const isActive = activeTab === tool.id;
                  return (
                    <button
                      key={tool.id}
                      onClick={() => selectTool(tool.id)}
                      className={`px-3 py-1.5 rounded-lg text-[9px] uppercase font-bold flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-xs font-extrabold"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <IconC className="w-3.5 h-3.5" />
                      <span>{tool.title}</span>
                    </button>
                  );
                })}
              </TabsSlider>

              {/* Info & Fullscreen toggle */}
              <button
                onClick={() => setIsInfoOpen(!isInfoOpen)}
                className={`p-2 rounded-lg border text-xs cursor-pointer transition-all flex-shrink-0 shadow-xs ${
                  isInfoOpen ? "bg-primary/15 border-primary text-primary font-bold" : "bg-card border-border text-muted-foreground hover:text-foreground"
                }`}
                title="Mostrar/Ocultar Guía de Uso Rápido"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Contextual "How to Use" Collapsible Card */}
            {isInfoOpen && (
              <div className="bg-card border border-border p-4 rounded-xl font-mono-tech text-[9px] space-y-2 relative animate-in fade-in shadow-xs">
                <div className="flex justify-between items-center border-b border-border pb-1.5">
                  <span className="text-foreground font-bold uppercase tracking-wider flex items-center gap-1.5">
                    💡 Guía Rápida: {currentTool.title}
                  </span>
                  <span className="text-muted-foreground text-[8px]">
                    Objetivo: {currentTool.tagline}
                  </span>
                </div>

                <div className="grid md:grid-cols-3 gap-3 pt-1">
                  {currentTool.howToUse.map((step, idx) => (
                    <div key={idx} className="p-2.5 bg-muted/40 border border-border rounded-lg text-muted-foreground font-sans text-[8.5px] leading-relaxed">
                      {step}
                    </div>
                  ))}
                </div>

                {currentTool.prTip && (
                  <div className="text-amber-400 text-[8px] pt-1 flex items-center gap-1">
                    <span>🇵🇷 <strong>Nota Puerto Rico:</strong> {currentTool.prTip}</span>
                  </div>
                )}
              </div>
            )}

            {/* Main Rendered Tool Component */}
            <div className="min-h-[500px] w-full">
              {renderActiveToolComponent()}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
